import React from 'react';
import { useTranslation } from 'react-i18next';
import type { EligibleLeg } from '@hero/api';
import type { OrderPODStatus } from '@hero/schema';
import { formatDateMain } from '@hero/core';
import { useOrderDrawerStore } from '@hero/hooks-shared';
import { cn } from '@hero/web-lib';
import {
  CostIcon,
  DateIcon,
  EyeIcon,
  LocationIcon,
  PlusIcon,
} from '@hero/web-icons';
import { Checkbox } from '../../../ui/Checkbox/Checkbox';
import { Card } from '../../../app/Card/Card';
import { Price } from '../../../app/Price/Price';
import { RouteArrowDisplay } from '../../../app/RouteArrowDisplay/RouteArrowDisplay';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../ui/Tooltip';

export interface EligibleOrderCardProps {
  leg: EligibleLeg;
  selected: boolean;
  onToggleSelected: () => void;
  className?: string;
}

/**
 * Mapping from the 7-value `OrderPODStatus` enum to Tailwind tone
 * pairs `[bg, border, text]`. Reused for the inline POD chip on
 * each eligible-order card. Tones mirror the platform's
 * status-badge palette (gray = no-op / done, amber = in flight /
 * needs action, blue = handoff in progress, green = success).
 */
const POD_TONE: Record<OrderPODStatus, { bg: string; border: string; text: string }> = {
  no_pod_required: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600' },
  not_received: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
  received: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
  under_review: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
  ready_to_be_sent: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
  sent: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700' },
  delivered: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
};

/**
 * Card row inside the {@link EligibleLegsDrawer} picker. Mirrors
 * the shape of the regular {@link OrderCard} (route + pickup
 * date + price), but specialised for the create-invoice flow:
 *
 * - The whole card is a selection target — clicking anywhere
 *   outside the eye button toggles the row's checkbox.
 * - The eye button stacks an `OrderDrawer` via the shared store
 *   (`useOrderDrawerStore.open(orderId)`) so the user can edit
 *   the order's price / details and have the picker re-validate
 *   on close. Cache invalidation goes through the
 *   `eligibleLegs` entity config (cross-busted by any order
 *   write), so we don't need to imperatively refetch here.
 * - A "+ fees" tick renders next to the price when the contract
 *   carries applied fees, with the fee subtotal in a tooltip.
 */
export const EligibleOrderCard: React.FC<EligibleOrderCardProps> = ({
  leg,
  selected,
  onToggleSelected,
  className,
}) => {
  const { t } = useTranslation(['m-invoice', 'c-main']);
  const openOrderDrawer = useOrderDrawerStore(s => s.open);

  const pickupName = leg.pickupCity ?? leg.pickupAddress ?? '';
  const dropoffName = leg.dropoffCity ?? leg.dropoffAddress ?? '';

  const pickupDateLabel = leg.pickupAt
    ? formatDateMain({ format: 'date_day', date: leg.pickupAt })
    : undefined;

  const ref = leg.orderRefMain || leg.orderRefShort || '—';
  const hasFees = !!leg.fees && leg.fees.count > 0;

  // Show the POD chip only when there's actually a paper trail to
  // talk about — either the order requires an original copy
  // return, or its lifecycle has progressed past `no_pod_required`
  // (someone marked it received / under_review / etc.). The
  // trivial "no_pod_required + doesn't require return" case stays
  // chip-free so the common scenario doesn't add visual noise.
  const showPodChip =
    leg.requiresOriginalCopy || leg.podStatus !== 'no_pod_required';
  const podTone = POD_TONE[leg.podStatus] ?? POD_TONE.no_pod_required;

  return (
    <Card
      onClick={onToggleSelected}
      className={cn('flex flex-col gap-2', className)}>
      {/* Row 1: checkbox + ref + route + view-order button */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Checkbox
            checked={selected}
            onClick={e => e.stopPropagation()}
            onCheckedChange={onToggleSelected}
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-semibold text-gray-900">{ref}</span>
            {(pickupName || dropoffName) && (
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <LocationIcon className="size-3.5 flex-shrink-0 text-gray-400" />
                <RouteArrowDisplay
                  origin={{ fullAddress: pickupName }}
                  destinations={[{ fullAddress: dropoffName }]}
                  size="small"
                />
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            openOrderDrawer(String(leg.orderId));
          }}
          className="inline-flex shrink-0 items-center justify-center rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={t('m-invoice:create_invoice.view_order', {
            defaultValue: 'View order',
          })}>
          <EyeIcon className="size-4" />
        </button>
      </div>

      {/* Row 2: pickup date + POD chip + price (+ fees) */}
      <div className="flex items-center justify-between gap-3 ps-7">
        <div className="flex flex-wrap items-center gap-2">
          {pickupDateLabel && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <DateIcon className="size-3.5 text-gray-400" />
              <span>
                {t('m-invoice:create_invoice.pickup_at', {
                  defaultValue: 'Pickup',
                })}
                : {pickupDateLabel}
              </span>
            </div>
          )}
          {showPodChip && (
            <span
              onClick={e => e.stopPropagation()}
              className={cn(
                'inline-flex cursor-default items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
                podTone.bg,
                podTone.border,
                podTone.text,
              )}>
              {t(`m-invoice:eligible_card.pod_status.${leg.podStatus}`, {
                defaultValue: leg.podStatus,
              })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <Price
            amount={leg.legTotal}
            currency={leg.currency}
            className="text-sm font-semibold text-gray-900"
          />
          {hasFees && leg.fees && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    onClick={e => e.stopPropagation()}
                    className="inline-flex cursor-default items-center gap-0.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                    <PlusIcon className="size-2.5" />
                    {t('m-invoice:create_invoice.fees_chip', {
                      count: leg.fees.count,
                      defaultValue: 'fees',
                    })}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="p-2 text-xs">
                  <div className="flex items-center gap-1.5">
                    <CostIcon className="size-3 text-amber-600" />
                    <span className="text-muted-foreground">
                      {t('m-invoice:create_invoice.fees_total', {
                        defaultValue: 'Fees',
                      })}
                      :
                    </span>
                    <Price amount={leg.fees.total} currency={leg.currency} />
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    </Card>
  );
};
